// A chapter: the ESV text and the commentary on it, side by side, on one page.
// There is no separate reader — scripture appears here because there is
// something to say about it, which is both the point of the site and what the
// ESV licence asks of us.
//
// There is also no separate editor. Turning on Edit swaps each block for its
// markdown source where it stands, so what you write always sits against the
// verses it is about.
import {
  ESV_COPYRIGHT,
  OPEN_TEXT_CREDITS,
  parseRef,
  RefError,
  toDisplay,
  toOsis,
  toSlug,
  type CommentaryCitationInput,
  type CommentaryInput,
  type CommentaryNoteInput,
  type CommentarySectionInput,
  type CommentaryStatus,
  type CommentaryView,
  type HighlightColor,
  type Ref,
  type TranslationId,
} from "@bibleunleashed/shared";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, useParams } from "wouter";
import { PreviewImageField } from "@/blog/PreviewImageField";
import { AdminBar } from "@/components/AdminBar";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { TagsInput } from "@/components/TagsInput";
import { MyMarks } from "@/library/MyMarks";
import { NoteEditor } from "@/library/NoteEditor";
import { useUser } from "@/lib/auth";
import { useTermMatcher } from "@/lib/glossary";
import { useSettings } from "@/lib/settings";
import { useCommentary, useDeleteCommentary, useSaveCommentary } from "@/lib/commentary";
import {
  useChapterLibrary,
  useClearHighlights,
  useDeleteBookmark,
  useSaveBookmark,
  useSaveHighlight,
  useSaveNote,
} from "@/lib/library";
import { AudioPlayer } from "@/reader/AudioPlayer";
import { paintHighlights } from "@/reader/highlights";
import { ContextMenu, useContextMenu, type MenuSpan, type MenuState } from "@/reader/ContextMenu";
import { TextSettings } from "@/reader/TextSettings";
import { alignRows } from "./align";
import { Citations } from "./Citations";
import { Editable } from "./Editable";
import { NoteBlock } from "./NoteBlock";
import { Passage, rangeDisplay, usePassage } from "./Passage";
import { StudyRail } from "./StudyRail";
import { VerseRangeField } from "./VerseRangeField";

/** Blocks carry a key while they are being edited; the server orders by position. */
type DraftNote = CommentaryNoteInput & { key: string };
type DraftSection = Omit<CommentarySectionInput, "notes"> & { key: string; notes: DraftNote[] };
interface Draft {
  title: string;
  introMd: string;
  tags: string[];
  image: string | null;
  status: CommentaryStatus;
  sections: DraftSection[];
  citations: CommentaryCitationInput[];
}

let nextKey = 0;
const newKey = () => `k${nextKey++}`;

const blankNote = (startVerse: number | null): DraftNote => ({ key: newKey(), startVerse, endVerse: null, bodyMd: "" });
const blankSection = (): DraftSection => ({
  key: newKey(),
  heading: "",
  startVerse: null,
  endVerse: null,
  bodyMd: "",
  notes: [],
  implicationsMd: "",
});

function toDraft(doc: CommentaryView | null, ref: Ref): Draft {
  if (!doc) {
    return {
      title: toDisplay(ref),
      introMd: "",
      tags: [],
      image: null,
      status: "draft",
      sections: [blankSection()],
      citations: [],
    };
  }
  return {
    title: doc.title,
    introMd: doc.introMd,
    tags: doc.tags,
    image: doc.image,
    status: doc.status,
    sections: doc.sections.map((s) => ({
      key: newKey(),
      heading: s.heading,
      startVerse: s.startVerse,
      endVerse: s.endVerse,
      bodyMd: s.bodyMd,
      implicationsMd: s.implicationsMd,
      notes: s.notes.map((n) => ({ key: newKey(), startVerse: n.startVerse, endVerse: n.endVerse, bodyMd: n.bodyMd })),
    })),
    citations: doc.citations.map((c) => ({ textMd: c.textMd, url: c.url, bibliographyId: c.bibliographyId })),
  };
}

const toInput = (draft: Draft): CommentaryInput => ({
  title: draft.title,
  introMd: draft.introMd,
  tags: draft.tags,
  image: draft.image,
  status: draft.status,
  sections: draft.sections.map(({ key: _k, notes, ...section }) => ({
    ...section,
    notes: notes.map(({ key: _nk, ...note }) => note),
  })),
  citations: draft.citations,
});

export function ChapterPage() {
  const params = useParams<{ book: string; chapter?: string }>();
  const { data: user } = useUser();
  const { settings, update: updateSettings } = useSettings();
  const isAdmin = user?.role === "admin";
  const [settingsOpen, setSettingsOpen] = useState(false);

  const parsed = useMemo(() => {
    try {
      const ref = parseRef(params.chapter ? `${params.book} ${params.chapter}` : params.book);
      return { ref: { book: ref.book, chapter: ref.chapter } as Ref, verse: ref.verse };
    } catch (err) {
      return { error: err instanceof RefError ? err.message : "Unknown chapter" };
    }
  }, [params.book, params.chapter]);
  const ref = parsed.ref;
  const osis = ref ? toOsis(ref) : undefined;

  const { data: doc, isLoading, error } = useCommentary(osis);
  const save = useSaveCommentary(osis ?? "");
  const remove = useDeleteCommentary(osis ?? "");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** The verse whose note is open in the editor, if any. */
  const [noteRef, setNoteRef] = useState<string | null>(null);
  /**
   * The verse the study rail is about. Clicking anywhere in a verse moves it,
   * which is the whole interaction: cross-references, the other translations
   * and the word study all follow the verse in front of the reader.
   */
  const [focusRef, setFocusRef] = useState<string | null>(null);

  // The member's own marks on this chapter: one query, shared by every block.
  const library = useChapterLibrary(osis);
  const saveHighlight = useSaveHighlight(osis ?? "");
  const clearHighlights = useClearHighlights(osis ?? "");
  const saveNote = useSaveNote(osis ?? "");
  const saveBookmark = useSaveBookmark(osis ?? "");
  const deleteBookmark = useDeleteBookmark(osis ?? "");
  const notedVerses = useMemo(() => library.notes.map((n) => n.ref), [library.notes]);
  const terms = useTermMatcher();
  const openNote = library.notes.find((n) => n.ref === noteRef);
  const reading = useRef<HTMLDivElement>(null);

  /**
   * The published chapter as a draft, computed once per document rather than
   * once per render. `toDraft` mints a fresh key for every section and note,
   * and calling it inline meant every render gave React new keys — so each
   * section subtree unmounted and remounted, the ESV HTML was rebuilt from
   * scratch, and anything applied to that markup afterwards was thrown away.
   */
  const published = useMemo(() => (doc && ref ? toDraft(doc, ref) : null), [doc, ref]);

  // Until a verse is clicked the rail is about the chapter's first commented
  // verse, so it has something to say the moment the page opens. `doc` changing
  // identity on a refetch does not move it — the verse number is what matters.
  const openingVerse = useMemo(() => doc?.sections.find((s) => s.startVerse !== null)?.startVerse ?? null, [doc]);
  useEffect(() => {
    setFocusRef(osis && openingVerse !== null ? `${osis}.${openingVerse}` : null);
  }, [osis, openingVerse]);

  const studyVerse = useCallback((e: React.MouseEvent) => {
    const verse = (e.target as Element).closest?.<HTMLElement>(".v[data-ref]");
    if (verse?.dataset.ref) setFocusRef(verse.dataset.ref);
  }, []);

  const parallel: TranslationId | undefined = settings.parallel === "none" ? undefined : settings.parallel;

  const jumpToVerse = useCallback((ref: string) => {
    document.querySelector(`.v[data-ref="${ref}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, []);

  const addHighlight = useCallback(
    (spans: MenuSpan[], color: HighlightColor) => {
      // A sweep across three verses is three rows: offsets only mean anything
      // inside one verse, and each can be cleared on its own later.
      for (const span of spans) saveHighlight.mutate({ ...span, color });
    },
    [saveHighlight],
  );

  const toggleBookmark = useCallback(
    (ref: string) => {
      const existing = library.bookmarks.find((b) => b.ref === ref);
      if (existing) deleteBookmark.mutate(existing.id);
      else saveBookmark.mutate({ ref, label: "", color: "yellow" });
    },
    [library.bookmarks, deleteBookmark, saveBookmark],
  );

  const storeNote = useCallback(
    async (bodyMd: string) => {
      if (!noteRef) return;
      await saveNote.mutateAsync({ ref: noteRef, bodyMd });
      setNoteRef(null);
    },
    [noteRef, saveNote],
  );

  const dirty = draft !== null && saved !== null && JSON.stringify(toInput(draft)) !== saved;

  useEffect(() => {
    document.title = doc?.title
      ? `${doc.title} · The Bible Unleashed`
      : osis
        ? `${toDisplay(parseRef(osis))} · The Bible Unleashed`
        : "The Bible Unleashed";
  }, [doc?.title, osis]);

  // Leaving with unsaved edits is nearly always a mistake.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    const close = () => setSettingsOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useContextMenu(setMenu);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    window.addEventListener("scroll", close, { passive: true });
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close);
    };
  }, [menu]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1400);
    return () => clearTimeout(t);
  }, [toast]);

  // #v5, or /commentary/nehemiah/1:5 — scroll once the verse is on the page.
  const wanted = parsed.verse ?? (Number(location.hash.match(/^#v(\d+)$/)?.[1]) || undefined);
  const { data: passage } = usePassage(doc ? osis : undefined);

  /**
   * The member's marks, painted over the whole reading column at once.
   *
   * Not inside `Passage`: a chapter is cut into a block per row, the CSS
   * highlight registry is one global thing, and a block that painted its own
   * would begin by clearing what every other block had just drawn — leaving
   * only the last block's, which usually has no highlights at all. A parent
   * layout effect runs after all its children's, so by the time this fires
   * every block has its verses in place.
   *
   * `terms` is here because a new glossary rebuilds every passage's markup,
   * and a highlight painted over the old text nodes would point at nothing.
   */
  useLayoutEffect(() => {
    const el = reading.current;
    if (!el) return;
    // "My highlights" off means the page is painted with none, not that the CSS
    // hides them: ::highlight() cannot be scoped to a container, so there is
    // nothing to hide them with.
    paintHighlights(el, settings.highlights ? library.highlights : []);
  }, [passage, published, library.highlights, settings.highlights, terms]);
  useEffect(() => {
    if (!wanted || !passage || !osis) return;
    const t = setTimeout(
      () => document.querySelector(`.v[data-ref="${osis}.${wanted}"]`)?.scrollIntoView({ block: "center" }),
      0,
    );
    return () => clearTimeout(t);
  }, [wanted, passage, osis]);

  const startEditing = useCallback(() => {
    if (!ref) return;
    const next = toDraft(doc ?? null, ref);
    setDraft(next);
    setSaved(JSON.stringify(toInput(next)));
  }, [doc, ref]);

  const stopEditing = useCallback(() => {
    if (dirty && !window.confirm("Discard the unsaved changes on this page?")) return;
    setDraft(null);
    setSaved(null);
  }, [dirty]);

  const patch = useCallback((change: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...change } : d)), []);
  const patchSection = useCallback(
    (key: string, change: Partial<Omit<CommentarySectionInput, "notes">>) =>
      setDraft((d) => (d ? { ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, ...change } : s)) } : d)),
    [],
  );
  const mapSection = useCallback(
    (key: string, fn: (section: DraftSection) => DraftSection) =>
      setDraft((d) => (d ? { ...d, sections: d.sections.map((s) => (s.key === key ? fn(s) : s)) } : d)),
    [],
  );
  const moveSection = useCallback(
    (index: number, by: -1 | 1) =>
      setDraft((d) => {
        if (!d) return d;
        const target = index + by;
        if (target < 0 || target >= d.sections.length) return d;
        const sections = [...d.sections];
        [sections[index], sections[target]] = [sections[target]!, sections[index]!];
        return { ...d, sections };
      }),
    [],
  );
  const addSection = useCallback(
    (after: number) =>
      setDraft((d) => {
        if (!d) return d;
        const sections = [...d.sections];
        sections.splice(after + 1, 0, blankSection());
        return { ...d, sections };
      }),
    [],
  );
  const removeSection = useCallback(
    (key: string) => setDraft((d) => (d ? { ...d, sections: d.sections.filter((s) => s.key !== key) } : d)),
    [],
  );

  const addNote = useCallback(
    (sectionKey: string, startVerse: number | null) =>
      mapSection(sectionKey, (s) => {
        const note = blankNote(startVerse);
        // Keep the stored order matching the reading order of the verses.
        const notes = [...s.notes, note].sort((a, b) => (a.startVerse ?? 0) - (b.startVerse ?? 0));
        return { ...s, notes };
      }),
    [mapSection],
  );
  const patchNote = useCallback(
    (sectionKey: string, noteKey: string, change: Partial<CommentaryNoteInput>) =>
      mapSection(sectionKey, (s) => ({
        ...s,
        notes: s.notes.map((n) => (n.key === noteKey ? { ...n, ...change } : n)),
      })),
    [mapSection],
  );
  const moveNote = useCallback(
    (sectionKey: string, noteKey: string, by: -1 | 1) =>
      mapSection(sectionKey, (s) => {
        const i = s.notes.findIndex((n) => n.key === noteKey);
        const target = i + by;
        if (i < 0 || target < 0 || target >= s.notes.length) return s;
        const notes = [...s.notes];
        [notes[i], notes[target]] = [notes[target]!, notes[i]!];
        return { ...s, notes };
      }),
    [mapSection],
  );
  const removeNote = useCallback(
    (sectionKey: string, noteKey: string) =>
      mapSection(sectionKey, (s) => ({ ...s, notes: s.notes.filter((n) => n.key !== noteKey) })),
    [mapSection],
  );

  const patchCitation = useCallback(
    (index: number, change: Partial<CommentaryCitationInput>) =>
      setDraft((d) => (d ? { ...d, citations: d.citations.map((c, i) => (i === index ? { ...c, ...change } : c)) } : d)),
    [],
  );
  const addCitation = useCallback(
    () => setDraft((d) => (d ? { ...d, citations: [...d.citations, { textMd: "", url: null, bibliographyId: null }] } : d)),
    [],
  );
  const moveCitation = useCallback(
    (index: number, by: -1 | 1) =>
      setDraft((d) => {
        if (!d) return d;
        const target = index + by;
        if (target < 0 || target >= d.citations.length) return d;
        const citations = [...d.citations];
        [citations[index], citations[target]] = [citations[target]!, citations[index]!];
        return { ...d, citations };
      }),
    [],
  );
  const removeCitation = useCallback(
    (index: number) => setDraft((d) => (d ? { ...d, citations: d.citations.filter((_, i) => i !== index) } : d)),
    [],
  );

  const doSave = useCallback(
    async (status?: CommentaryStatus) => {
      if (!draft) return;
      const next = status ? { ...draft, status } : draft;
      const input = toInput(next);
      const result = await save.mutateAsync(input);
      setDraft({ ...next, status: result.status });
      setSaved(JSON.stringify({ ...input, status: result.status }));
    },
    [draft, save],
  );

  const doDelete = useCallback(async () => {
    if (!window.confirm("Delete this chapter for good?")) return;
    await remove.mutateAsync();
    setDraft(null);
    setSaved(null);
  }, [remove]);

  if (!ref || !osis) {
    return (
      <Shell>
        <p className="font-serif text-xl">{parsed.error}</p>
        <Link href="/commentary" className="mt-6 inline-block text-accent">
          Every chapter we have
        </Link>
      </Shell>
    );
  }

  const canonical = `/commentary/${toSlug(ref)}`;
  const here = `/commentary/${params.book}${params.chapter ? `/${params.chapter}` : ""}`;
  if (here !== canonical) return <Redirect to={canonical + (parsed.verse ? `#v${parsed.verse}` : location.hash)} replace />;

  if (isLoading) {
    return (
      <Shell>
        <p className="font-serif text-ink-3">Loading…</p>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <p className="font-serif text-xl">Couldn’t load this chapter.</p>
        <p className="mt-2 text-[13px] text-ink-2">{error.message}</p>
      </Shell>
    );
  }
  if (!doc && !draft) {
    // No commentary means no scripture: the ESV text is here to be discussed,
    // and a bare chapter would be neither useful nor within the licence.
    return (
      <Shell>
        <p className="text-xs tracking-[0.12em] text-accent uppercase">{toDisplay(ref)}</p>
        <h1 className="mt-2 font-serif text-[36px] font-semibold">Not written up yet</h1>
        <p className="mt-3 font-serif text-lg text-ink-2">
          {isAdmin
            ? "Start it — it opens as a draft, invisible until you publish."
            : `${toDisplay(ref)} hasn’t been worked through yet. Chapters appear here once there is something to say about them.`}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {isAdmin && (
            <button onClick={startEditing} className="rounded-md bg-accent px-4 py-2 text-[14px] font-medium text-white">
              Start writing
            </button>
          )}
          <Link href="/commentary" className="rounded-md border border-ink px-4 py-2 text-[14px] text-ink">
            Every chapter we have
          </Link>
          <a
            href={`https://www.esv.org/${toDisplay(ref).replace(/ /g, "+")}`}
            rel="noopener noreferrer"
            target="_blank"
            className="rounded-md px-4 py-2 text-[14px] text-ink-2 hover:text-ink"
          >
            Read {toDisplay(ref)} on esv.org →
          </a>
        </div>
      </Shell>
    );
  }

  const view: Draft = draft ?? published!;
  const editing = draft !== null;
  const showsScripture = view.sections.some((s) => s.startVerse !== null);
  const outline = view.sections.map((section, i) => ({ id: `sec-${i + 1}`, heading: section.heading }));

  return (
    <div className="min-h-screen">
      <Header current={ref} onToggleSettings={() => setSettingsOpen((v) => !v)} />
      {settingsOpen && <TextSettings onClose={() => setSettingsOpen(false)} />}
      <main className={`mx-auto max-w-[1400px] px-6 py-12 lg:px-10 ${isAdmin ? "pb-28" : ""}`}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[2fr_1fr] lg:gap-16">
          <div ref={reading} onClick={studyVerse} className="min-w-0">
            <header className="mx-auto max-w-[760px]">
              <div className="flex items-center gap-3">
                <p className="text-xs tracking-[0.12em] text-accent uppercase">{toDisplay(ref)}</p>
                {showsScripture && <AudioPlayer osis={osis} display={toDisplay(ref)} />}
              </div>
              <h1 className="mt-2 font-serif text-[38px] leading-[1.1] font-semibold tracking-[-0.015em]">
                <Editable
                  label="Title"
                  inline
                  editing={editing}
                  value={view.title}
                  placeholder={`Title for ${toDisplay(ref)}`}
                  onChange={(title) => patch({ title })}
                />
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {view.status === "draft" && (
                  <span className="rounded-full border border-rule px-2.5 py-0.5 font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
                    Draft — only you can see this
                  </span>
                )}
                {editing ? (
                  <TagsInput tags={view.tags} onChange={(tags) => patch({ tags })} />
                ) : (
                  view.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-paper-2 px-2.5 py-0.5 font-sans text-[11px] text-ink-3">
                      {tag}
                    </span>
                  ))
                )}
              </div>
              {editing && (
                <div className="mt-5">
                  <PreviewImageField image={view.image} onChange={(image) => patch({ image })} />
                </div>
              )}
              <div className="mt-6 font-serif text-[18px] leading-relaxed">
                <Editable
                  label="Introduction"
                  editing={editing}
                  value={view.introMd}
                  placeholder="An introduction to the chapter…"
                  onChange={(introMd) => patch({ introMd })}
                />
              </div>
            </header>

            {view.sections.map((section, i) => {
              const rows = alignRows(section.startVerse, section.endVerse, section.notes);
              return (
                <section
                  key={section.key}
                  id={`sec-${i + 1}`}
                  className={`mt-14 scroll-mt-20 ${editing ? "rounded-lg border border-dashed border-rule p-4" : ""}`}
                >
                  {editing && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-rule pb-3">
                      <VerseRangeField
                        startVerse={section.startVerse}
                        endVerse={section.endVerse}
                        onChange={(range) => patchSection(section.key, range)}
                      />
                      <span className="ml-auto flex items-center gap-1">
                        <IconButton label="Move section up" icon="up" disabled={i === 0} onClick={() => moveSection(i, -1)} />
                        <IconButton
                          label="Move section down"
                          icon="down"
                          disabled={i === view.sections.length - 1}
                          onClick={() => moveSection(i, 1)}
                        />
                        <IconButton label="Add a section below" icon="plus" onClick={() => addSection(i)} />
                        <IconButton label="Delete this section" icon="trash" onClick={() => removeSection(section.key)} />
                      </span>
                    </div>
                  )}
                  {section.startVerse !== null && (
                    <p className="mb-1 font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
                      {rangeDisplay(ref.book, ref.chapter, section.startVerse, section.endVerse ?? section.startVerse)} · ESV
                    </p>
                  )}
                  <h2 className="font-serif text-[26px] font-semibold tracking-[-0.01em]">
                    <Editable
                      label={`Heading for section ${i + 1}`}
                      inline
                      editing={editing}
                      value={section.heading}
                      placeholder="Section heading"
                      onChange={(heading) => patchSection(section.key, { heading })}
                    />
                  </h2>
                  {(editing || section.bodyMd.trim()) && (
                    <div className="mt-3 max-w-[760px] font-serif text-[17px] leading-relaxed text-ink-2">
                      <Editable
                        label={`Introduction to section ${i + 1}`}
                        editing={editing}
                        value={section.bodyMd}
                        placeholder="Anything to say about the section as a whole…"
                        onChange={(bodyMd) => patchSection(section.key, { bodyMd })}
                      />
                    </div>
                  )}

                  <div className="mt-6 space-y-8">
                    {rows.map((row, r) => (
                      <div key={`${row.startVerse ?? "none"}-${r}`} className="min-w-0">
                        {row.startVerse !== null && (
                          <Passage
                            osis={osis}
                            book={ref.book}
                            chapter={ref.chapter}
                            startVerse={row.startVerse}
                            endVerse={row.endVerse ?? row.startVerse}
                            notedVerses={notedVerses}
                            focusRef={focusRef}
                            parallel={parallel}
                          />
                        )}
                        {(row.notes.length > 0 || editing) && (
                          <div className="mt-4 space-y-4">
                            {row.notes.map((note, n) => (
                              <NoteBlock
                                key={note.key}
                                note={note}
                                label={`Note on ${section.heading || `section ${i + 1}`}, ${n + 1}`}
                                editing={editing}
                                onChange={(change) => patchNote(section.key, note.key, change)}
                                onMove={(by) => moveNote(section.key, note.key, by)}
                                onRemove={() => removeNote(section.key, note.key)}
                                canMoveUp={section.notes[0]?.key !== note.key}
                                canMoveDown={section.notes[section.notes.length - 1]?.key !== note.key}
                              />
                            ))}
                            {editing && (
                              <button
                                onClick={() => addNote(section.key, row.startVerse)}
                                className="ml-2 flex w-[calc(100%-0.5rem)] items-center justify-center gap-1.5 rounded-md border border-dashed border-rule py-2 text-[12px] text-ink-3 hover:border-ink-3 hover:text-ink lg:ml-6 lg:w-[calc(100%-1.5rem)]"
                              >
                                <Icon name="plus" width="14" height="14" />
                                {row.startVerse === null ? "Add a note" : `Note on v. ${row.startVerse}`}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {(editing || section.implicationsMd.trim()) && (
                    <div className="mt-8 max-w-[760px] rounded-lg bg-paper-2 px-5 py-4">
                      <h3 className="mb-2 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">Counseling implications</h3>
                      <div className="font-serif text-[17px] leading-relaxed">
                        <Editable
                          label={`Counseling implications for section ${i + 1}`}
                          editing={editing}
                          value={section.implicationsMd}
                          placeholder="Counseling implications…"
                          onChange={(implicationsMd) => patchSection(section.key, { implicationsMd })}
                        />
                      </div>
                    </div>
                  )}
                </section>
              );
            })}

            {editing && (
              <button
                onClick={() => addSection(view.sections.length - 1)}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-rule py-3 text-[13px] text-ink-2 hover:border-ink-3 hover:text-ink"
              >
                <Icon name="plus" width="15" height="15" /> Add a section
              </button>
            )}

            <div className="mx-auto max-w-[760px]">
              <Citations
                citations={view.citations}
                editing={editing}
                onChange={patchCitation}
                onAdd={addCitation}
                onMove={moveCitation}
                onRemove={removeCitation}
              />
              {showsScripture && (
                <div className="mt-12 space-y-2 border-t border-rule pt-4 text-[11.5px] leading-normal text-ink-3">
                  <p>
                    {ESV_COPYRIGHT}{" "}
                    <a href="https://www.esv.org" target="_blank" rel="noopener noreferrer" className="text-accent">
                      esv.org
                    </a>
                  </p>
                  <p>{OPEN_TEXT_CREDITS}</p>
                </div>
              )}
            </div>
          </div>
          <StudyRail
            outline={outline}
            focus={focusRef}
            parallel={settings.parallel}
            onParallel={(value) => updateSettings({ parallel: value })}
          >
            {showsScripture && (
              <MyMarks
                signedIn={Boolean(user)}
                notes={library.notes}
                bookmarks={library.bookmarks}
                onOpenNote={setNoteRef}
                onJump={jumpToVerse}
              />
            )}
          </StudyRail>
        </div>
      </main>

      {menu && (
        <ContextMenu
          menu={menu}
          onClose={() => setMenu(null)}
          onToast={setToast}
          actions={{
            signedIn: Boolean(user),
            highlighted: library.highlights.some((h) => h.ref === menu.ref),
            bookmarked: library.bookmarks.some((b) => b.ref === menu.ref),
            hasNote: notedVerses.includes(menu.ref),
            onStudy: setFocusRef,
            onHighlight: addHighlight,
            onClearHighlight: (r) => clearHighlights.mutate(r),
            onNote: setNoteRef,
            onBookmark: toggleBookmark,
          }}
        />
      )}
      {noteRef && (
        <NoteEditor
          verseRef={noteRef}
          initial={openNote?.bodyMd ?? ""}
          saving={saveNote.isPending}
          onSave={(bodyMd) => void storeNote(bodyMd)}
          onClose={() => setNoteRef(null)}
        />
      )}
      <div
        aria-live="polite"
        className={`pointer-events-none fixed bottom-7 left-1/2 z-40 -translate-x-1/2 rounded-full bg-ink px-4 py-2 text-[13px] text-paper transition-opacity ${toast ? "opacity-100" : "opacity-0"}`}
      >
        {toast}
      </div>

      {isAdmin && (
        <AdminBar
          editing={editing}
          dirty={dirty}
          status={view.status}
          saving={save.isPending}
          error={save.error?.message ?? remove.error?.message}
          exists={Boolean(doc)}
          onEdit={startEditing}
          onDone={stopEditing}
          onSave={() => void doSave()}
          onPublish={() => void doSave(view.status === "published" ? "draft" : "published")}
          onDelete={() => void doDelete()}
        />
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[720px] px-6 py-20">{children}</main>
    </div>
  );
}

function IconButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: "up" | "down" | "plus" | "trash";
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid size-7 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <Icon name={icon} width="15" height="15" />
    </button>
  );
}
